/**
 * Одна карточка МП целиком: фото, текст, коммерция, поля площадки.
 *
 * Поля показываются с именами площадки — их даёт справочник характеристик, тот
 * же, по которому собирается запрос к WB или OZON. Прежняя вкладка печатала
 * плоский дамп снимка (`technical.nutrition.energy_value → 364`), по которому
 * нельзя было понять ни что это за поле, ни обязательно ли оно.
 */

import React from 'react'
import { ExternalLink, ArrowLeftRight, ChevronRight } from 'lucide-react'
import { Badge } from '../../../../components/ui/Badge'
import { CopyButton } from '../../../../components/ui/CopyButton'
import { assertNever, MARKETPLACES } from '../../../../shared/marketplaces'
import type { MarketplaceCardDto, MarketplaceFieldLabel, MarketplaceFieldLabels } from '../../../pipeline/api'
import { MarketplacePhotos } from './MarketplacePhotos'
import { MarketplaceReasons } from './MarketplaceReasonList'
import {
  cardPhotos,
  cardPrice,
  cardState,
  cardStock,
  rowBadge,
  storefrontUrl,
} from './marketplaceCardState'

/**
 * Единственный лимит длины, подтверждённый документацией площадки, — наименование
 * Wildberries. Для описания WB и для OZON контракт не зафиксирован
 * (`services/seo-service/src/domain/text-content-profile.ts`), поэтому там
 * показываем только фактическую длину: выдуманный лимит хуже отсутствующего.
 */
const WB_TITLE_LIMIT = 60
const WB_TITLE_SOURCE = 'WB Content API, проверено 02.08.2026'

const money = (value?: number | null) =>
  value == null ? '—' : `${Number(value).toLocaleString('ru-RU')} ₽`

/**
 * `MarketplaceCardDto['marketplace']` (`features/pipeline/api.ts`) — три
 * площадки конвейера карточек: `wb`, `ozon`, `ym` (Этап 2,
 * `docs/SPEC_2026-09-13_YANDEX_MARKET.md`, §4). `switch` с `assertNever`
 * вместо тернарника — без риска молча увести новую площадку в ветку OZON,
 * если тип когда-нибудь расширится.
 */
type DetailMarketplace = MarketplaceCardDto['marketplace']

function detailBadgeTone(code: DetailMarketplace): 'purple' | 'blue' | 'yellow' | 'gray' {
  switch (code) {
    case 'wb': return 'purple'
    case 'ozon': return 'blue'
    case 'ym': return 'yellow'
    // Сайт — канал, а не площадка, но его карточки бэкенд отдаёт сюда же.
    case 'site': return 'gray'
    default: return assertNever(code)
  }
}

function detailShortLabel(code: DetailMarketplace): string {
  switch (code) {
    case 'wb': return 'WB'
    case 'ozon': return 'OZON'
    case 'ym': return MARKETPLACES.ym.label
    case 'site': return 'Сайт'
    default: return assertNever(code)
  }
}

/**
 * Разделы полей: те же группы, что человек проверяет перед публикацией.
 *
 * Ключи ym совпадают с ozon: `snapshotFieldValues` (design-service) собирает
 * их из общего технического снимка макета (`technical.*`), а не из
 * площадко-специфичных колонок — поле «Вес нетто» одно и то же независимо от
 * того, куда едет карточка.
 */
const GROUPS: Array<{ title: string; keys: Record<'wb' | 'ozon' | 'ym', string[]> }> = [
  {
    title: 'Состав, пищевая ценность и хранение',
    keys: {
      wb: ['categoryComposition', 'country_of_origin', 'shelf_life', 'min_storage_temp',
        'max_storage_temp', 'energy_value', 'protein', 'fat_per_100g', 'carbs'],
      ozon: ['composition', 'country', 'shelf_life_days', 'storage_conditions',
        'min_storage_temp', 'max_storage_temp'],
      ym: ['composition', 'country', 'shelf_life_days', 'storage_conditions',
        'min_storage_temp', 'max_storage_temp', 'energy_value', 'protein', 'fat_per_100g', 'carbs'],
    },
  },
  {
    title: 'Вес и габариты',
    keys: {
      wb: ['weight_net', 'dimensions.itemWidth', 'dimensions.itemDepth', 'dimensions.itemHeight'],
      ozon: ['weight_net', 'weight', 'width', 'height', 'depth'],
      ym: ['weight_net', 'weight', 'width', 'height', 'depth'],
    },
  },
  {
    title: 'Документы и коды',
    keys: {
      wb: ['declaration_number', 'cert_reg_date', 'cert_expiry_date', 'certificate_number', 'tnved'],
      ozon: ['tnved'],
      ym: ['tnved'],
    },
  },
  {
    title: 'Свойства для фильтров площадки',
    keys: {
      wb: ['decorType', 'glazeType', 'features', 'giftType', 'packaging'],
      ozon: ['decor_type', 'product_type', 'units_per_item'],
      ym: ['packaging', 'units_per_item'],
    },
  },
]

interface Props {
  card: MarketplaceCardDto
  twin: MarketplaceCardDto | null
  fieldLabels: MarketplaceFieldLabels | null
  onOpenVisual: (urls: string[], index: number, article: string) => void
  onBack: () => void
  onCompare: () => void
}

/** Кнопка копирования в плотных блоках карточки: рамка и подпись «копировать», логика — общая. */
const CopyChip: React.FC<{ value: string; title: string }> = ({ value, title }) => (
  <CopyButton
    value={value}
    label={title}
    size="xs"
    withText
    className="border border-gray-200 px-1.5 py-0.5 dark:border-zinc-700"
  />
)

const Tile: React.FC<{ label: string; children: React.ReactNode; tone?: 'accent' | 'missing' }> = ({
  label,
  children,
  tone,
}) => (
  <div
    className={`rounded-lg border p-2.5 ${
      tone === 'accent'
        ? 'border-indigo-200 bg-indigo-50/70 dark:border-indigo-900 dark:bg-indigo-950/30'
        : tone === 'missing'
          ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30'
          : 'border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900'
    }`}
  >
    <div className="text-2xs text-gray-500 dark:text-zinc-400">{label}</div>
    <div className={`mt-0.5 text-sm font-semibold ${tone === 'missing' ? 'text-red-600 dark:text-red-400' : ''}`}>
      {children}
    </div>
  </div>
)

export const MarketplaceCardDetail: React.FC<Props> = ({
  card,
  twin,
  fieldLabels,
  onOpenVisual,
  onBack,
  onCompare,
}) => {
  const snapshot = card.revision_snapshot
  const photos = cardPhotos(card)
  const badge = rowBadge(card)
  const state = cardState(card)
  const url = storefrontUrl(card)
  const title = snapshot?.title || card.title || null
  const description = snapshot?.description || null
  const values = snapshot?.field_values ?? {}
  const labels: MarketplaceFieldLabel[] = card.marketplace === 'site'
    ? []
    : fieldLabels?.[card.marketplace] ?? []
  const labelByKey = new Map(labels.map((item) => [item.key, item]))
  const category = (snapshot?.marketplace_category ?? {}) as Record<string, unknown>
  const titleOverLimit = card.marketplace === 'wb' && !!title && title.length > WB_TITLE_LIMIT

  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800">
      <header className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3.5 py-2.5 dark:border-zinc-800">
        <Badge variant={detailBadgeTone(card.marketplace)}>
          {detailShortLabel(card.marketplace)}
        </Badge>
        <span className="font-mono text-sm font-semibold">{card.article}</span>
        <Badge variant="gray">{card.seller_name || `Кабинет ${card.seller_id}`}</Badge>
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <div className="ml-auto flex items-center gap-2">
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
            >
              Открыть на витрине <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {twin && (
            <button
              type="button"
              onClick={onCompare}
              className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-gray-300 dark:border-zinc-700 dark:text-zinc-300"
            >
              <ArrowLeftRight className="h-3 w-3" />
              сравнить с {detailShortLabel(twin.marketplace)}
            </button>
          )}
          <button
            type="button"
            onClick={onBack}
            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:border-gray-300 dark:border-zinc-700 dark:text-zinc-300"
          >
            ← к обзору
          </button>
        </div>
      </header>

      <div className="space-y-4 p-3.5">
        <MarketplaceReasons card={card} />

        <section>
          <h4 className="mb-2 text-2xs font-bold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Фото {photos.length > 0 && <span className="font-normal">· {photos.length} шт</span>}
          </h4>
          <MarketplacePhotos urls={photos} article={card.article} onOpen={onOpenVisual} />
        </section>

        <section>
          <h4 className="mb-2 text-2xs font-bold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Текст {snapshot ? '· из замороженной ревизии' : '· с витрины площадки'}
          </h4>
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-2xs font-semibold text-gray-500 dark:text-zinc-400">
                  Наименование
                </span>
                {title && <CopyChip value={title} title="наименование" />}
                <span
                  className={`ml-auto text-2xs ${titleOverLimit ? 'font-bold text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-zinc-500'}`}
                  title={card.marketplace === 'wb' ? WB_TITLE_SOURCE : 'подтверждённого лимита нет'}
                >
                  {title
                    ? card.marketplace === 'wb'
                      ? `${title.length} из ${WB_TITLE_LIMIT}`
                      : `${title.length} символов`
                    : '—'}
                </span>
              </div>
              <p className="mt-0.5 text-sm font-semibold">{title || '—'}</p>
              {card.marketplace !== 'wb' && (
                <p className="mt-1 text-2xs text-gray-400 dark:text-zinc-500">
                  Лимит длины у этой площадки документацией не подтверждён — показываем фактическую
                  длину.
                </p>
              )}
            </div>

            {description && (
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xs font-semibold text-gray-500 dark:text-zinc-400">
                    Описание
                  </span>
                  <CopyChip value={description} title="описание" />
                  <span className="ml-auto text-2xs text-gray-400 dark:text-zinc-500">
                    {description.length} символов
                  </span>
                </div>
                <p className="mt-0.5 whitespace-pre-wrap text-[0.8125rem] leading-6 text-gray-700 dark:text-zinc-300">
                  {description}
                </p>
              </div>
            )}
          </div>
        </section>

        <section>
          <h4 className="mb-2 text-2xs font-bold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
            Коммерция и раздел витрины
            {state === 'frozen' && <span className="font-normal"> · заморожено вместе с составом</span>}
          </h4>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-2">
            <Tile label="Цена" tone={cardPrice(card) == null ? 'missing' : 'accent'}>
              {cardPrice(card) == null ? 'не заведена' : money(cardPrice(card))}
            </Tile>
            <Tile label="Плановый остаток">{cardStock(card) ?? '—'}</Tile>
            <Tile label="Категория PIM">
              <span className="font-mono">{snapshot?.category_prefix || card.category_prefix || '—'}</span>
            </Tile>
            {(() => {
              // Три площадки — три раздела категории; свой switch, а не
              // тернарник «wb или ozon», чтобы ym не унаследовал чужой
              // (ozon_type_id у карточек Маркета не заполняется никогда, и
              // тернарник молча показывал бы «не заполнены» вместо своей плитки).
              switch (card.marketplace) {
                case 'wb':
                  return (
                    <Tile label="Предмет Wildberries" tone={category.subject_id ? undefined : 'missing'}>
                      <span className="font-mono text-xs">{String(category.subject_id ?? 'не задан')}</span>
                    </Tile>
                  )
                case 'ozon':
                  return (
                    <Tile label="Категория и тип OZON" tone={category.ozon_type_id ? undefined : 'missing'}>
                      <span className="font-mono text-xs">
                        {category.ozon_type_id
                          ? `${category.ozon_description_category_id ?? '—'} / ${category.ozon_type_id}`
                          : 'не заполнены'}
                      </span>
                    </Tile>
                  )
                case 'ym':
                  return (
                    <Tile label="Категория Маркета" tone={category.ym_category_id ? undefined : 'missing'}>
                      <span className="font-mono text-xs">{String(category.ym_category_id ?? 'не заполнена')}</span>
                    </Tile>
                  )
                case 'site':
                  // У сайта нет категории площадки — плитки нет.
                  return null
                default:
                  return assertNever(card.marketplace)
              }
            })()}
            <Tile label="ТН ВЭД" tone={category.tnved ? undefined : 'missing'}>
              <span className="font-mono text-xs">{String(category.tnved ?? 'не заполнен')}</span>
            </Tile>
          </div>
        </section>

        {snapshot && (
          <section>
            <h4 className="mb-2 text-2xs font-bold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
              Поля карточки на площадке
              <span className="font-normal">
                {' '}
                · имена из справочника {detailShortLabel(card.marketplace)}
              </span>
            </h4>
            <div className="space-y-1.5">
              {GROUPS.map((group) => {
                // У канала сайта групп характеристик площадки нет.
                const rows = (card.marketplace === 'site' ? ([] as string[]) : group.keys[card.marketplace])
                  .map((key) => ({ key, label: labelByKey.get(key), value: values[key] }))
                  .filter((row) => row.value !== undefined)
                if (rows.length === 0) return null
                return (
                  <details
                    key={group.title}
                    className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-800"
                  >
                    <summary className="flex cursor-pointer list-none items-center gap-2 bg-gray-50 px-3 py-2 text-xs font-semibold dark:bg-zinc-900">
                      <ChevronRight className="h-3 w-3 shrink-0 transition-transform [details[open]_&]:rotate-90" />
                      {group.title}
                      <span className="ml-auto text-2xs font-normal text-gray-400 dark:text-zinc-500">
                        {rows.length}
                      </span>
                    </summary>
                    <dl className="grid grid-cols-1 gap-x-5 px-3 py-2 sm:grid-cols-2">
                      {rows.map((row) => (
                        <div
                          key={row.key}
                          className="flex items-baseline justify-between gap-3 border-b border-dashed border-gray-200 py-1.5 text-xs last:border-0 dark:border-zinc-800"
                        >
                          <dt className="text-gray-500 dark:text-zinc-400">
                            {row.label?.name || row.key}
                            {row.label?.required && (
                              <span className="ml-0.5 text-red-500" title="обязательное поле площадки">
                                *
                              </span>
                            )}
                            {!row.label && (
                              <span className="ml-1 italic text-gray-400 dark:text-zinc-500">
                                нет в справочнике
                              </span>
                            )}
                          </dt>
                          <dd className="text-right font-medium">
                            {row.value === null || row.value === '' ? (
                              <span className="font-normal text-red-600 dark:text-red-400">
                                не заполнено
                              </span>
                            ) : (
                              String(row.value)
                                .split(';')
                                .map((part) => part.trim())
                                .filter(Boolean)
                                .map((part) => (
                                  <span
                                    key={part}
                                    className="ml-1 inline-block rounded border border-gray-200 px-1.5 py-0.5 text-2xs font-normal dark:border-zinc-700"
                                  >
                                    {part}
                                  </span>
                                ))
                            )}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                )
              })}
            </div>

            <details className="mt-2">
              <summary className="cursor-pointer list-none py-1 text-2xs text-gray-400 dark:text-zinc-500">
                Служебные данные ревизии
              </summary>
              <dl className="mt-1 grid grid-cols-1 gap-x-5 sm:grid-cols-2">
                {[
                  ['Ревизия карточки', `#${snapshot.id}`],
                  ['Прогон конвейера', snapshot.run_id || '—'],
                  [
                    'SEO-контент',
                    `${snapshot.seo_content_id ?? '—'} · ревизия ${snapshot.seo_revision ?? '—'} · id ${snapshot.seo_revision_id ?? '—'}`,
                  ],
                  ['Кабинет', String((snapshot.target as any)?.cabinet ?? '—')],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="flex items-baseline justify-between gap-3 border-b border-dashed border-gray-200 py-1 text-2xs dark:border-zinc-800"
                  >
                    <dt className="text-gray-400 dark:text-zinc-500">{label}</dt>
                    <dd className="break-all text-right font-mono text-gray-500 dark:text-zinc-400">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
              <CopyButton
                value={`MCR ${snapshot.id} | run ${snapshot.run_id} | ${card.article} ${card.marketplace} | seo ${snapshot.seo_content_id}/${snapshot.seo_revision_id}`}
                label="данные для поддержки"
                size="xs"
                withText="скопировать для поддержки"
                className="mt-2 border border-gray-200 px-1.5 py-0.5 dark:border-zinc-700"
              />
            </details>
          </section>
        )}
      </div>
    </section>
  )
}

export default MarketplaceCardDetail
